import { useNavigate } from "react-router-dom";
import { useTrend } from "../hooks/useQueries";
import SparklineBars from "./SparklineBars";
import { fmtPct } from "../utils/format";

interface StockCardProps {
  exchange: string;
  ticker: string;
  name: string;
}

/**
 * Homepage stock grid card — shows ticker, company name,
 * 4 quarters of revenue sparkline bars, and YoY growth badge.
 */
export default function StockCard({ exchange, ticker, name }: StockCardProps) {
  const navigate = useNavigate();
  const { data, isLoading } = useTrend(exchange, ticker, "revenue", 4);
  const trendData = data?.data ?? [];

  // YoY change from the newest point
  const changePct =
    trendData.length > 0 ? trendData[0].change_pct : null;

  return (
    <div
      className="metric-card flex flex-col gap-2 rounded-xl bg-card-dark border border-card-border p-4 cursor-pointer hover:border-primary/30 transition-colors group shadow-sm shadow-black/50"
      onClick={() => navigate(`/stocks/${exchange}/${ticker.toLowerCase()}/overview`)}
    >
      {/* Header */}
      <div className="flex items-baseline gap-2">
        <span className="font-mono font-bold text-white text-sm">
          {ticker}
        </span>
        <span className="text-xs text-text-secondary truncate">{name}</span>
      </div>

      {/* Sparkline bars (non-interactive — whole card is a link) */}
      <div className="pointer-events-none">
        {isLoading ? (
          <div className="h-16 mt-2 flex items-end gap-1.5">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="flex-1 rounded-t-sm bg-card-border animate-pulse"
                style={{ height: `${20 + i * 15}%` }}
              />
            ))}
          </div>
        ) : (
          <SparklineBars data={trendData} bgActive="bg-primary" height="h-16" />
        )}
      </div>

      {/* Growth badge — bottom right */}
      <div className="flex justify-end">
        {changePct != null ? (
          <span
            className={`text-xs font-medium px-1.5 py-0.5 rounded ${
              changePct >= 0
                ? "text-accent-green bg-accent-green/10"
                : "text-accent-red bg-accent-red/10"
            }`}
          >
            {fmtPct(changePct)}
          </span>
        ) : (
          <span className="text-xs text-text-secondary">—</span>
        )}
      </div>
    </div>
  );
}
